import { PropTypes } from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { Menu, placements } from '@ge/components/menu';
import { Severity } from '@ge/components/severity';
import { DynamicTable } from '@ge/components/table';
import { TableArrow } from '@ge/components/table/table';
import { useColumnState } from '@ge/components/table/use-column-state';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import {
  TaskColumns,
  DefaultTaskColumns,
  TaskColumnDefs,
} from '@ge/feat-analyze/models/configure-case-template';
import { SortDirection } from '@ge/models/constants';

const Container = styled.div`
  .column-group-header {
    display: none;
  }
  .column-header {
    top: 0;
    border-color: transparent !important;
    height: auto;
    vertical-align: middle;
    padding-top: 5px;
    background: none;
  }
  th {
    padding: 0;
  }
  tbody {
    tr {
      height: auto;

      td {
        padding: 8px;
        vertical-align: middle;
      }
    }
    tr:nth-child(even) {
      background: ${(props) => props.theme.table.evenRowColor};
    }
    tr:nth-child(odd) {
      background: ${(props) => props.theme.table.oddRowColor};
    }
  }
`;

const HoverText = styled.div`
  background: ${({ theme }) => theme.tooltip.backgroundColor};
  color: #ffffff;
  max-width: 400px;
  padding: 10px;
  margin: 10px;
  border-radius: 6px;
  box-shadow: ${({ theme }) => theme.tooltip.boxShadow};
  font-family: 'Museo Sans';
  font-size: 13px;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
`;

const HoverContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DescriptionContainer = styled.div`
  display: flex;
  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EditButton = styled.button``;
const DeleteButton = styled.button``;
const CaseDetailButton = styled.button`
  padding: 8px 0;
  width: 100%;
`;

const ButtonIcon = styled(Icon).attrs((props) => ({
  size: 10,
  color: props.theme.entityDetails.headerIconColor,
}))``;

const ColumnHover = ({ children, value }) => {
  const [anchorElement, setAnchorElement] = useState(null);

  const handleShowMenu = (e) => {
    setAnchorElement(e.currentTarget);
  };

  const handleMenuClose = () => setAnchorElement(null);

  return (
    <HoverContainer onMouseEnter={handleShowMenu} onMouseLeave={handleMenuClose}>
      {children}
      {anchorElement && value && (
        <Menu
          anchorEl={anchorElement}
          open={true}
          onClose={handleMenuClose}
          placement={placements.TOP_START}
          zIndex={9999}
        >
          <HoverText>{value}</HoverText>
        </Menu>
      )}
    </HoverContainer>
  );
};

ColumnHover.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string,
};

const TasksTable = ({ values, onClickEdit, onClickDelete, isEditing }) => {
  const { t, ready } = useTranslation(['analyze.asset'], {
    useSuspense: false,
  });

  const customCellFn = useCallback(
    (columnKey, cellValue, value) => {
      switch (columnKey) {
        case TaskColumns.PRIORITY:
          return <Severity level={cellValue?.toLowerCase()} />;
        case TaskColumns.TITLE:
        case TaskColumns.DESCRIPTION:
          return (
            <ColumnHover value={cellValue}>
              <DescriptionContainer>
                <span>{cellValue}</span>
              </DescriptionContainer>
            </ColumnHover>
          );
        case TaskColumns.EDIT:
          return (
            isEditing && (
              <EditButton onClick={() => onClickEdit(value.id)}>
                <ButtonIcon icon={Icons.PENCIL} />
              </EditButton>
            )
          );
        case TaskColumns.DELETE:
          return (
            isEditing && (
              <DeleteButton onClick={() => onClickDelete(value.id)}>
                <ButtonIcon icon={Icons.TRASH} />
              </DeleteButton>
            )
          );
        case TaskColumns.DUE_DATE_OFFSET:
          return cellValue;
        case TaskColumns.TASK_DETAIL:
          // eslint-disable-next-line jsx-a11y/control-has-associated-label
          return (
            !isEditing && (
              <CaseDetailButton type="button" onClick={() => onClickEdit(value.id)}>
                <TableArrow />
              </CaseDetailButton>
            )
          );
        default:
          return null;
      }
    },
    [onClickEdit, onClickDelete, isEditing],
  );

  const {
    visibleCols: columns,
    sortDirection,
    sortMetric,
  } = useColumnState({
    columnDefs: TaskColumnDefs,
    defaultCols: DefaultTaskColumns,
    defaultSortMetric: TaskColumns.TITLE,
    defaultSortDirection: SortDirection.DESC,
  });

  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortMetric, sortDirection],
  );

  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: TaskColumnDefs,
    cellValueMapFn: (val) => val,
    customCellFn,
    sortedDirection,
    defaultCellBorder: true,
  });

  if (!ready) return null;

  return (
    <Container>
      <DynamicTable
        columns={
          isEditing
            ? columns.filter((column) => column.id !== TaskColumns.GROUP_TASK_DETAIL)
            : columns.filter(
                (column) =>
                  column.id !== TaskColumns.GROUP_EDIT && column.id !== TaskColumns.GROUP_DELETE,
              )
        }
        values={values}
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        onValueSelect={() => {}}
        rowKeyProperty={TaskColumns.ID}
        scrollable={values.length > 5}
      />
    </Container>
  );
};

TasksTable.propTypes = {
  values: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  scrollable: PropTypes.bool,
  assetId: PropTypes.string,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  height: PropTypes.number,
  isEditing: PropTypes.bool,
};

TasksTable.defaultProps = {
  values: [],
  scrollable: false,
  onClickEdit: () => null,
  onClickDelete: () => null,
  height: 400,
  isEditing: true,
};

export default TasksTable;
